import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { SIGNED_URL_FETCH_AS, useSignedUrlFetch } from '@/hooks/files';
import {
  clearCbullshitcodingFadeTimeout,
  getCodeFileLines,
  type CbullshitcodingContentStateControls,
} from './CbullshitcodingContentState';

interface UseCbullshitcodingCodeFileContentInput
  extends CbullshitcodingContentStateControls {
  codeFile?: string;
}

interface CodeFileContentFetchInput
  extends CbullshitcodingContentStateControls {
  codeFile: string;
  signedUrlFetch: SignedUrlFetch;
}

interface ApplyCodeFileLinesInput
  extends CbullshitcodingContentStateControls {
  lines: string[];
}

type SignedUrlFetch = ReturnType<typeof useSignedUrlFetch>;

function useLatestSignedUrlFetchRef({
  signedUrlFetch,
}: {
  signedUrlFetch: SignedUrlFetch;
}): MutableRefObject<SignedUrlFetch> {
  const signedUrlFetchRef = useRef(signedUrlFetch);

  useEffect(() => {
    signedUrlFetchRef.current = signedUrlFetch;
  }, [signedUrlFetch]);

  return signedUrlFetchRef;
}

function clearLoadedCodeFileContent(
  input: CbullshitcodingContentStateControls,
): void {
  if (input.hasContentRef.current) {
    input.setIsFadingOut(true);
    input.fadeTimeoutRef.current = setTimeout(() => {
      input.setLoadedStrings([]);
      input.resetTypingState();
      input.hasContentRef.current = false;
      input.setIsFadingOut(false);
    }, input.fadeDurationMs);
    return;
  }

  input.setLoadedStrings([]);
}

function applyCodeFileLines(input: ApplyCodeFileLinesInput): void {
  if (input.hasContentRef.current) {
    input.setIsFadingOut(true);
    input.setPendingStrings(input.lines);
    return;
  }

  input.setLoadedStrings(input.lines);
}

function startCodeFileContentFetch(input: CodeFileContentFetchInput): () => void {
  let cancelled = false;

  input.signedUrlFetch.invoke(
    { url: input.codeFile, as: SIGNED_URL_FETCH_AS.Text },
    {
      onSuccess: (result) => {
        if (cancelled || !('text' in result)) {
          return;
        }

        applyCodeFileLines({
          ...input,
          lines: getCodeFileLines({ text: result.text }),
        });
      },
      onError: (error) => {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
      },
    },
  );

  return () => {
    cancelled = true;
    input.signedUrlFetch.reset();
    clearCbullshitcodingFadeTimeout({ timeoutRef: input.fadeTimeoutRef });
  };
}

export function useCbullshitcodingCodeFileContent({
  codeFile,
  fadeDurationMs,
  fadeTimeoutRef,
  hasContentRef,
  resetTypingState,
  setIsFadingOut,
  setLoadedStrings,
  setPendingStrings,
}: UseCbullshitcodingCodeFileContentInput): void {
  const signedUrlFetch = useSignedUrlFetch();
  const signedUrlFetchRef = useLatestSignedUrlFetchRef({ signedUrlFetch });

  useEffect(() => {
    const contentControls: CbullshitcodingContentStateControls = {
      fadeDurationMs,
      fadeTimeoutRef,
      hasContentRef,
      resetTypingState,
      setIsFadingOut,
      setLoadedStrings,
      setPendingStrings,
    };

    if (!codeFile) {
      clearLoadedCodeFileContent(contentControls);
      return undefined;
    }

    return startCodeFileContentFetch({
      ...contentControls,
      codeFile,
      signedUrlFetch: signedUrlFetchRef.current,
    });
  }, [
    codeFile,
    fadeDurationMs,
    fadeTimeoutRef,
    hasContentRef,
    resetTypingState,
    setIsFadingOut,
    setLoadedStrings,
    setPendingStrings,
    signedUrlFetchRef,
  ]);
}
